import React from 'react';
import { Box, TextField, Typography, Select, MenuItem, FormControlLabel, Switch, Tooltip } from '@mui/material';

const PLATFORM_OPTIONS = [
  { label: 'IBM AIX on POWER Systems (64-Bit)', value: 'AIX.x64' },
  { label: 'LINUX ARM (aarch64)', value: 'ARM.x64' },
  { label: 'Oracle Solaris on SPARC (64-Bit)', value: 'SPARC.x64' },
  { label: 'Oracle Solaris on x86-64 (64-Bit)', value: 'SOLARIS.x64' },
  { label: 'Microsoft Windows x64 (64-bit)', value: 'WINDOWS.X64' },
  { label: 'Linux x86-64', value: 'LINUX.X64' },
];

interface BasicInformationProps {
  sid: string;
  setSid: (v: string) => void;
  sourceHome: string;
  setSourceHome: (v: string) => void;
  targetHome: string;
  setTargetHome: (v: string) => void;
  platform: string;
  setPlatform: (v: string) => void;
  placeholders: {
    sid: string;
    source_home: string;
    target_home: string;
  };
  patchKey: string;
  logDir: string;
  setLogDir: (v: string) => void;
  winCredential?: string;
  setWinCredential?: (v: string) => void;
  deleteWinCredentialFile?: boolean;
  setDeleteWinCredentialFile?: (v: boolean) => void;
  operationType: 'patch' | 'upgrade';
}

const BasicInformation: React.FC<BasicInformationProps> = ({
  sid, setSid,
  sourceHome, setSourceHome,
  targetHome, setTargetHome,
  platform, setPlatform,
  placeholders,
  patchKey,
  logDir,
  setLogDir,
  winCredential,
  setWinCredential,
  deleteWinCredentialFile,
  setDeleteWinCredentialFile,
  operationType
}) => {
  // Floating placeholder logic for each field
  const [sidTouched, setSidTouched] = React.useState(false);
  const [sourceHomeTouched, setSourceHomeTouched] = React.useState(false);
  const [targetHomeTouched, setTargetHomeTouched] = React.useState(false);
  const [logDirTouched, setLogDirTouched] = React.useState(false);

  // Reset touched states when platform or patchKey changes
  React.useEffect(() => {
    const resetTouchedStates = () => {
      setSidTouched(false);
      setSourceHomeTouched(false);
      setTargetHomeTouched(false);
      setLogDirTouched(false);
    };
    resetTouchedStates();
  }, [platform, patchKey]);

  // Dynamic placeholders for Windows
  let logDirPlaceholder = platform === 'WINDOWS.X64' 
    ? `C:\\Oracle\\AutoUpgrade\\${sid || placeholders.sid}_Upgrade\\log` 
    : `/home/oracle/autoupgrade/${sid || placeholders.sid}_Upgrade/log`;
  let sourceHomePlaceholder = platform === 'WINDOWS.X64'
    ? (operationType === 'upgrade' ? 'C:\\Oracle\\Product\\12.2.0.1\\dbhome_1' : 'C:\\Oracle\\Product\\19.15\\dbhome_1')
    : (operationType === 'upgrade' ? '/u01/app/oracle/product/12.2.0.1/dbhome_1' : '/u01/app/oracle/product/19.15/dbhome_1');
  let targetHomePlaceholder = platform === 'WINDOWS.X64' ? 'C:\\Oracle\\Product\\19.27\\dbhome_1' : placeholders.target_home;
  if (platform === 'WINDOWS.X64') {
    logDirPlaceholder = logDirPlaceholder.replace(/^C(?!:)/, 'C:');
    sourceHomePlaceholder = sourceHomePlaceholder.replace(/^C(?!:)/, 'C:');
    targetHomePlaceholder = targetHomePlaceholder.replace(/^C(?!:)/, 'C:');
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>SID:</Typography>
        <TextField
          fullWidth
          size="small"
          value={sid}
          onChange={e => { setSidTouched(true); setSid(e.target.value); }}
          onFocus={() => { if (!sidTouched && sid === '') setSid(''); setSidTouched(true); }}
          placeholder={placeholders.sid}
          InputProps={{
            sx: {
              '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color: !sidTouched && sid === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
              background: 'transparent',
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Source Home:</Typography>
        <TextField
          fullWidth
          size="small"
          value={sourceHome}
          onChange={e => { setSourceHomeTouched(true); setSourceHome(e.target.value); }}
          onFocus={() => { if (!sourceHomeTouched && sourceHome === '') setSourceHome(''); setSourceHomeTouched(true); }}
          placeholder={sourceHomePlaceholder}
          InputProps={{
            sx: {
              '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color: !sourceHomeTouched && sourceHome === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
              background: 'transparent',
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Target Home:</Typography>
        <TextField
          fullWidth
          size="small"
          value={targetHome}
          onChange={e => { setTargetHomeTouched(true); setTargetHome(e.target.value); }}
          onFocus={() => { if (!targetHomeTouched && targetHome === '') setTargetHome(''); setTargetHomeTouched(true); }}
          placeholder={targetHomePlaceholder}
          InputProps={{
            sx: {
              '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color: !targetHomeTouched && targetHome === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
              background: 'transparent',
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>{(sid || placeholders.sid)} Upgrade Log Dir:</Typography>
        <TextField
          fullWidth
          size="small"
          value={logDir}
          onChange={e => { setLogDirTouched(true); setLogDir(e.target.value); }}
          onFocus={() => { if (!logDirTouched && logDir === '') setLogDir(''); setLogDirTouched(true); }}
          placeholder={logDirPlaceholder}
          InputProps={{
            sx: {
              '& input::placeholder': { color: '#8a8a8a', opacity: 1 },
              '& .MuiInputBase-input': {
                color: !logDirTouched && logDir === '' ? '#8a8a8a' : theme => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              fontSize: 15,
              background: 'transparent',
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'none' }}>
          <Select
            fullWidth
            size="small"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            sx={{
              '& .MuiSelect-select': {
                color: '#6c757d',
                fontSize: 15,
              },
            }}
          >
            {PLATFORM_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Tooltip
          title="Enable to specify a Windows Credential File for AutoUpgrade."
          placement="right"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: theme => theme.palette.mode === 'dark' ? '#f5f5f5' : '#23272f',
                color: theme => theme.palette.mode === 'dark' ? '#222' : '#fff',
                fontSize: 15,
                borderRadius: 2,
                boxShadow: 3,
                px: 2,
                py: 1,
                fontWeight: 400,
                minWidth: 320,
              },
            },
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={platform === 'WINDOWS.X64'}
                onChange={e => setPlatform(e.target.checked ? 'WINDOWS.X64' : 'LINUX.X64')}
                color="primary"
              />
            }
            label={<Typography sx={{ fontWeight: 600 }}>Microsoft Windows</Typography>}
          />
        </Tooltip>
      </Box>

      {platform === 'WINDOWS.X64' && winCredential !== undefined && setWinCredential !== undefined && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Windows Credential File:</Typography>
            <TextField
              fullWidth
              size="small"
              value={winCredential}
              onChange={e => setWinCredential(e.target.value)}
              placeholder={`C:\\Oracle\\AutoUpgrade\\Credential\\${patchKey}.xml`}
              InputProps={{
                sx: {
                  '&::placeholder': { color: '#b0b0b0', opacity: 1 },
                  color: '#6c757d',
                  fontSize: 15,
                },
              }}
            />
          </Box>
          {deleteWinCredentialFile !== undefined && setDeleteWinCredentialFile !== undefined && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={deleteWinCredentialFile}
                    onChange={e => setDeleteWinCredentialFile(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography sx={{ fontWeight: 600 }}>Delete Windows Credential File</Typography>}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BasicInformation; 